import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Polyline, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Box, Typography } from "@mui/material";
import { brandColors } from "../../theme";
import { fetchRoute } from "./foodUtils";

const emojiIcon = (emoji, background) =>
  new L.DivIcon({
    html: `<div style="
      width: 38px; height: 38px; border-radius: 50%; background: ${background};
      display: flex; align-items: center; justify-content: center; font-size: 20px;
      border: 3px solid white; box-shadow: 0 3px 10px rgba(0,0,0,0.35);
    ">${emoji}</div>`,
    className: "",
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });

const STORE_ICON = emojiIcon("🏪", "#ffffff");
const HOME_ICON = emojiIcon("🏠", "#ffffff");
const RIDER_ICON = emojiIcon("🛵", "#ffd54f");

// Encuadra todos los puntos cada vez que cambian (p. ej. al moverse el repartidor).
const FitBounds = ({ points }) => {
  const map = useMap();
  const key = points.map((p) => p.join(",")).join(";");
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 15);
    else map.fitBounds(points, { padding: [40, 40], maxZoom: 16 });
  }, [map, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

// Mapa del recorrido de un pedido con envío: local, destino y, si el local
// comparte su ubicación, el repartidor. La ruta va desde el repartidor (o el
// local, antes de salir) hasta el destino.
const FoodOrderMap = ({ order, height = 260 }) => {
  const store = order.restaurantLocation;
  const home = order.deliveryLocation;
  const rider = order.status === "on_the_way" ? order.riderLocation : null;
  const origin = rider || store;
  const [route, setRoute] = useState(null);

  // La ruta se recalcula cuando el repartidor se movió unos ~100 m, no en cada lectura del GPS.
  const originKey = origin ? `${origin.lat.toFixed(3)},${origin.lng.toFixed(3)}` : "";
  useEffect(() => {
    if (!origin || !home) return undefined;
    let cancelled = false;
    fetchRoute(origin, home).then((result) => !cancelled && setRoute(result));
    return () => {
      cancelled = true;
    };
  }, [originKey, home?.lat, home?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const points = useMemo(
    () => [store, home, rider].filter(Boolean).map((p) => [p.lat, p.lng]),
    [store, home, rider]
  );

  if (!home && !store) return null;

  const line = route?.points || (origin && home ? [[origin.lat, origin.lng], [home.lat, home.lng]] : null);

  return (
    <Box>
      <Box sx={{ height, borderRadius: 3, overflow: "hidden", border: 1, borderColor: "divider" }}>
        <MapContainer center={points[0]} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          {line && <Polyline positions={line} pathOptions={{ color: brandColors.accent, weight: 5, opacity: 0.85, dashArray: route ? null : "8 8" }} />}
          {store && <Marker position={[store.lat, store.lng]} icon={STORE_ICON} />}
          {home && <Marker position={[home.lat, home.lng]} icon={HOME_ICON} />}
          {rider && <Marker position={[rider.lat, rider.lng]} icon={RIDER_ICON} zIndexOffset={1000} />}
        </MapContainer>
      </Box>
      {rider && route?.minutes && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
          🛵 Llega en unos {route.minutes} min
        </Typography>
      )}
      {order.status === "on_the_way" && !rider && (
        <Typography variant="caption" color="text.secondary">
          El repartidor todavía no compartió su ubicación.
        </Typography>
      )}
    </Box>
  );
};

export default FoodOrderMap;
